import React, { useState, useEffect } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { ChatContainer } from './ChatContainer';
import { Message } from '../../types/chat';
import { chatService } from '../../lib/chat/chatService';
import { X } from 'lucide-react';

interface ChatOverlayProps {
  tournamentId: string;
  isAdmin?: boolean;
  show: boolean;
}

export function ChatOverlay({ tournamentId, isAdmin, show }: ChatOverlayProps) {
  const { showChat, toggleChat } = useChat();
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadTime, setLastReadTime] = useState(Date.now());
  const [lastMessageId, setLastMessageId] = useState<string | null>(null);

  // Reset unread count when chat is opened
  useEffect(() => {
    if (showChat) {
      setUnreadCount(0);
      setLastReadTime(Date.now());
      setLastMessageId(null);
    }
  }, [showChat]);

  // Subscribe to new messages
  useEffect(() => {
    const handleNewMessage = (message: Message) => {
      // Only count if chat is closed and it's not a duplicate message
      if (!showChat && message.id !== lastMessageId) {
        // Only increment for messages after last read time
        const messageTime = new Date(message.createdAt).getTime();
        if (messageTime > lastReadTime) {
          setUnreadCount(prev => prev + 1);
          setLastMessageId(message.id);
        }
      }
    };

    chatService.onMessage(handleNewMessage);

    return () => {
      // Cleanup will be handled by chatService
    };
  }, [showChat, lastReadTime, lastMessageId]);

  if (!show) return null;

  return (
    <div className={`fixed inset-y-0 right-0 w-[85vw] sm:w-96 max-w-md bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
      showChat ? 'translate-x-0' : 'translate-x-full'
    } z-[9999] pb-[env(safe-area-inset-bottom)]`}>
      <div className="absolute -left-10 sm:-left-12 top-1/2 -translate-y-1/2">
        <button
          onClick={() => {
            toggleChat();
            if (!showChat) {
              setUnreadCount(0);
              setLastReadTime(Date.now());
              setLastMessageId(null);
            }
          }}
          className="bg-primary-600 text-white p-2 rounded-l-lg shadow-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 min-h-[44px]"
          aria-label={showChat ? 'Hide chat' : 'Show chat'}
        >
          {unreadCount > 0 && !showChat && (
            <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount > 99 ? '99+' : unreadCount}
            </div>
          )}
          {showChat ? '→' : '←'}
        </button>
      </div>
      <div className="absolute top-4 right-4">
        <button
          onClick={() => {
            toggleChat();
            setUnreadCount(0);
            setLastReadTime(Date.now());
            setLastMessageId(null);
          }}
          className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded-full p-1"
          aria-label="Close chat"
        >
          <X className="w-5 h-5" />
        </button>
      </div>
      <ChatContainer
        tournamentId={tournamentId}
        isAdmin={isAdmin}
      />
    </div>
  );
}